/**
 * Cost Calculation Utilities for Volcano Workflows
 * Calculates estimated and actual costs for workflow executions
 */

import type { WorkflowStep, Provider, StepExecution } from '../types/volcano.types';

// ============================================================================
// Provider Cost Configuration
// ============================================================================

interface ModelCostConfig {
  input_cost_per_million: number;
  output_cost_per_million: number;
}

const MODEL_COSTS: Record<string, ModelCostConfig> = {
  // Anthropic Claude Models
  'claude-haiku-4-5-20251001': {
    input_cost_per_million: 1.0,
    output_cost_per_million: 5.0,
  },
  'claude-3-5-sonnet-20241022': {
    input_cost_per_million: 3.0,
    output_cost_per_million: 15.0,
  },
  'claude-3-opus-20240229': {
    input_cost_per_million: 15.0,
    output_cost_per_million: 75.0,
  },
  
  // OpenAI GPT Models
  'gpt-4o': {
    input_cost_per_million: 5.0,
    output_cost_per_million: 15.0,
  },
  'gpt-4': {
    input_cost_per_million: 30.0,
    output_cost_per_million: 60.0,
  },
  'gpt-4-turbo': {
    input_cost_per_million: 10.0,
    output_cost_per_million: 30.0,
  },
};

// Default costs by provider (if model not specified)
const DEFAULT_COSTS: Record<Provider, ModelCostConfig> = {
  anthropic: MODEL_COSTS['claude-haiku-4-5-20251001'],
  openai: MODEL_COSTS['gpt-4o'],
};

// ============================================================================
// Cost Calculation Functions
// ============================================================================

/**
 * Get cost configuration for a specific model or provider default
 */
export function getModelCostConfig(provider: Provider, model?: string): ModelCostConfig {
  if (model && MODEL_COSTS[model]) {
    return MODEL_COSTS[model];
  }
  // Return provider default or fallback to anthropic if provider unknown
  return DEFAULT_COSTS[provider] || DEFAULT_COSTS.anthropic;
}

/**
 * Calculate cost for a single step execution
 */
export function calculateStepCost(
  provider: Provider,
  model: string | undefined,
  inputTokens: number,
  outputTokens: number
): number {
  const costConfig = getModelCostConfig(provider, model);
  
  // Safety check - should never happen with fallback in getModelCostConfig
  if (!costConfig) {
    console.warn('[Cost Calculator] No cost config found, using default');
    return 0;
  }
  
  const inputCost = (inputTokens / 1_000_000) * costConfig.input_cost_per_million;
  const outputCost = (outputTokens / 1_000_000) * costConfig.output_cost_per_million;
  
  return inputCost + outputCost;
}

/**
 * Estimate cost for a workflow step (before execution)
 * Uses average token counts as heuristics
 */
export function estimateStepCost(step: WorkflowStep): { min: number; max: number; avg: number } {
  // Only LLM providers have meaningful token-cost estimates.
  if (step.provider !== 'anthropic' && step.provider !== 'openai') {
    return { min: 0, max: 0, avg: 0 };
  }

  const costConfig = getModelCostConfig(step.provider, step.model);
  
  // Heuristic: Estimate token counts based on prompt length
  const promptLength = step.prompt?.length || 0;
  const estimatedInputTokens = Math.ceil(promptLength / 4); // ~4 chars per token
  
  // Output token estimates (conservative ranges)
  const minOutputTokens = 50;
  const avgOutputTokens = 500;
  const maxOutputTokens = 2000;
  
  const minCost = calculateStepCost(step.provider, step.model, estimatedInputTokens, minOutputTokens);
  const avgCost = calculateStepCost(step.provider, step.model, estimatedInputTokens, avgOutputTokens);
  const maxCost = calculateStepCost(step.provider, step.model, estimatedInputTokens, maxOutputTokens);
  
  return { min: minCost, max: maxCost, avg: avgCost };
}

/**
 * Estimate total workflow cost
 */
export function estimateWorkflowCost(stepsInput: unknown): {
  min: number;
  max: number;
  avg: number;
  per_step: Array<{ min: number; max: number; avg: number }>;
} {
  const steps = Array.isArray(stepsInput) ? stepsInput : [];
  let totalMin = 0;
  let totalMax = 0;
  let totalAvg = 0;
  const perStep: Array<{ min: number; max: number; avg: number }> = [];
  
  for (const step of steps) {
    const stepCost = estimateStepCost(step);
    totalMin += stepCost.min;
    totalMax += stepCost.max;
    totalAvg += stepCost.avg;
    perStep.push(stepCost);
  }
  
  return {
    min: totalMin,
    max: totalMax,
    avg: totalAvg,
    per_step: perStep,
  };
}

/**
 * Calculate actual cost from step execution
 */
export function calculateActualStepCost(execution: StepExecution): number {
  if (!execution.input_tokens || !execution.output_tokens) {
    return 0;
  }
  
  return calculateStepCost(
    execution.provider,
    execution.model,
    execution.input_tokens,
    execution.output_tokens
  );
}

/**
 * Calculate total actual cost from all step executions
 */
export function calculateTotalExecutionCost(stepExecutions: StepExecution[]): number {
  return stepExecutions.reduce((total, step) => {
    return total + calculateActualStepCost(step);
  }, 0);
}

/**
 * Format cost as currency string
 */
export function formatCost(cost: number): string {
  if (cost < 0.001) {
    return '<$0.001';
  }
  if (cost < 0.01) {
    return `$${cost.toFixed(4)}`;
  }
  if (cost < 1) {
    return `$${cost.toFixed(3)}`;
  }
  return `$${cost.toFixed(2)}`;
}

/**
 * Format cost range as string
 */
export function formatCostRange(min: number, max: number): string {
  return `${formatCost(min)}-${formatCost(max)}`;
}

/**
 * Get cost tier for color coding
 */
export function getCostTier(cost: number): 'low' | 'medium' | 'high' {
  if (cost < 0.01) return 'low';
  if (cost < 0.10) return 'medium';
  return 'high';
}

/**
 * Get cost tier color (for UI badges)
 */
export function getCostTierColor(cost: number): string {
  const tier = getCostTier(cost);
  switch (tier) {
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'high': return 'bg-red-100 text-red-800 border-red-200';
  }
}

/**
 * Calculate potential savings from optimization
 */
export function calculateSavings(
  currentCost: number,
  optimizedCost: number,
  executionsPerMonth: number
): {
  savings_per_execution: number;
  savings_per_month: number;
  savings_percentage: number;
} {
  const savingsPerExecution = currentCost - optimizedCost;
  const savingsPerMonth = savingsPerExecution * executionsPerMonth;
  const savingsPercentage = ((savingsPerExecution / currentCost) * 100);
  
  return {
    savings_per_execution: savingsPerExecution,
    savings_per_month: savingsPerMonth,
    savings_percentage: savingsPercentage,
  };
}

/**
 * Compare costs between two providers/models
 */
export function compareCosts(
  provider1: Provider,
  model1: string | undefined,
  provider2: Provider,
  model2: string | undefined,
  estimatedInputTokens: number = 1000,
  estimatedOutputTokens: number = 500
): {
  cost1: number;
  cost2: number;
  difference: number;
  cheaper: 'provider1' | 'provider2';
  savings_percentage: number;
} {
  const cost1 = calculateStepCost(provider1, model1, estimatedInputTokens, estimatedOutputTokens);
  const cost2 = calculateStepCost(provider2, model2, estimatedInputTokens, estimatedOutputTokens);
  
  const difference = Math.abs(cost1 - cost2);
  const cheaper = cost1 < cost2 ? 'provider1' : 'provider2';
  const expensiveCost = Math.max(cost1, cost2);
  const savingsPercentage = (difference / expensiveCost) * 100;
  
  return {
    cost1,
    cost2,
    difference,
    cheaper,
    savings_percentage: savingsPercentage,
  };
}

/**
 * Get all available models for a provider
 */
export function getProviderModels(provider: Provider): string[] {
  const prefix = provider === 'anthropic' ? 'claude' : 'gpt';
  return Object.keys(MODEL_COSTS).filter(model => model.startsWith(prefix));
}

/**
 * Get model display name (human-readable)
 */
export function getModelDisplayName(model: string): string {
  const displayNames: Record<string, string> = {
    'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
    'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
    'claude-3-opus-20240229': 'Claude 3 Opus',
    'gpt-4o': 'GPT-4o',
    'gpt-4': 'GPT-4',
    'gpt-4-turbo': 'GPT-4 Turbo',
  };
  
  return displayNames[model] || model;
}

